import type { UseFormReturnType } from '@mantine/form';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useState } from 'react';
import { z } from 'zod';

import { FEATURE } from '~/common/enums/feature.enum';
import { formatDollars } from '~/domains/common/utils/formatters';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { getReferralCode } from '~/services/identity/unauthenticated/identity.service.api';
import dt from '~/testing';

import { getPromoCode } from '../services/promo-codes.api';
import type { BonusModalTemplateProps } from '../components/PromotionBonusModal/types';
import {
  parsePromoTicketsFromPromoCode,
  parsePromoTicketFromReferralCode,
} from '../../../utils/parsePromoTickets';

type UseReferralCodeValidityProps = {
  form: UseFormReturnType<{ promoOrReferralCode: string }>;
};

type ValidationResult = {
  codeError?: string;
  promoCode?: string;
  referralCode?: string;
  bonusModalProps?: Omit<BonusModalTemplateProps, 'onAction' | 'isOpen' | 'onClose'>;
};

export const useValidatePromoOrReferalCode = ({ form }: UseReferralCodeValidityProps) => {
  const { t } = useTranslation('promotions');
  const [isFetching, setIsFetching] = useState(false);
  const isPromoCodesEnabled = useIsFeatureEnabled(FEATURE.ENABLE_PROMO_CODES);
  const codeValue = form.values.promoOrReferralCode;

  const handleValidateCode = useCallback(async (): Promise<ValidationResult> => {
    if (!codeValue || codeValue.trim().length === 0) return {};

    const promoCodeSchema = z
      .string()
      .trim()
      .min(3)
      .max(25)
      .regex(/^[A-Za-z0-9_-]+$/);

    try {
      promoCodeSchema.parse(codeValue);
    } catch (error) {
      return {
        referralCode: undefined,
        promoCode: undefined,
        bonusModalProps: undefined,
        codeError: t('myPromotions.promoCodes.promoCodeForm.invalidError'),
      };
    }

    setIsFetching(true);

    try {
      // Throwing this error to break the try/catch block and go directly to the referral code validation
      if (!isPromoCodesEnabled) throw new Error('Promo codes are not enabled');

      const data = await getPromoCode(codeValue);

      return {
        promoCode: data.promoCode,
        bonusModalProps: {
          promoCode: data.promoCode,
          title: t('bonusModal.title__hasPlural', {
            count: parsePromoTicketsFromPromoCode(data, t).length,
          }),
          description: t('bonusModal.descriptionRegistrationPromo'),
          bonuses: {
            promotions: parsePromoTicketsFromPromoCode(data, t),
            expiresAt: data.expiresAt,
          },
          actionButtonLabel: t('bonusModal.actionAcknowledge'),
          actionButtonTestId: dt.promotions.bonusModal.actionButton,
        },
      };
    } catch (promoCodeError) {
      try {
        if (codeValue.length === 6) {
          const { data } = await getReferralCode(codeValue);

          return {
            referralCode: data.code,
            bonusModalProps: {
              promoCode: data.code,
              title: t('bonusModal.titleRaf'),
              description: t('bonusModal.descriptionReferral', { amount: formatDollars(10) }),
              bonuses: {
                promotions: parsePromoTicketFromReferralCode(data.code, t),
                expiresAt: undefined,
              },
              actionButtonLabel: t('bonusModal.actionAcknowledge'),
              actionButtonTestId: dt.promotions.bonusModal.actionButton,
            },
          };
        }

        throw promoCodeError;
      } catch (referralCodeError) {
        const fieldError =
          promoCodeError.response?.data?.message ||
          referralCodeError.response?.data?.message ||
          t('referral.claimError');

        return {
          referralCode: undefined,
          promoCode: undefined,
          bonusModalProps: undefined,
          codeError: fieldError,
        };
      }
    } finally {
      setIsFetching(false);
    }
  }, [codeValue, isPromoCodesEnabled]);

  return {
    handleValidateCode,
    isValidating: isFetching,
  };
};
